import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { supportChatsAPI } from '../services/api'
import { useTenantDirectory } from '../lib/tenantDirectory'
import { toTenantModel, tenantView, RANK, formatRelative } from '../lib/support'
import { Dot, Tag, PageHeader, SectionCard, Spinner, ErrorBanner } from '../components/ui'

// "Today" — start-of-shift triage. Replaces the old platform dashboard at /.

const firstUserMessage = (chat) => chat.messages?.find((m) => m.role === 'user')?.content || ''

const KpiCard = ({ label, tone, value, hint, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title={hint}
    className="card flex flex-col gap-0.5 px-4 py-3 text-left transition-colors hover:bg-admin-200"
  >
    <span className="flex items-center gap-1.5 text-xs text-admin-600">
      <Dot tone={tone} />
      {label}
    </span>
    <span className="text-[26px] font-semibold leading-[1.15] tabular-nums">{value}</span>
  </button>
)

export const DashboardPage = () => {
  const navigate = useNavigate()
  const { tenants, loading, error } = useTenantDirectory()
  const [chats, setChats] = useState(null)

  useEffect(() => {
    const since = new Date(Date.now() - 86400000).toISOString().split('T')[0]
    supportChatsAPI
      .getAll({ per_page: 100, date_from: since })
      .then((res) => setChats(res.data?.data || []))
      .catch(() => setChats([]))
  }, [])

  const views = useMemo(() => tenants.map((t) => tenantView(toTenantModel(t))), [tenants])
  const attentionViews = views.filter((v) => v.needsAttention)

  // The list endpoint doesn't report WhatsApp state / owner sign-in yet. Show "—" rather
  // than a reassuring 0 until it does.
  const reportsWa = tenants.some((t) => t.whatsapp_state !== undefined)
  const reportsLogin = tenants.some((t) => t.owner_can_login !== undefined)

  const kpis = [
    {
      label: 'WhatsApp fora do ar',
      tone: 'crit',
      value: reportsWa ? views.filter((v) => v.wa.tone === 'crit').length : '—',
      hint: reportsWa ? undefined : 'A API da lista de estúdios ainda não informa o estado do WhatsApp — verifique cada estúdio.',
      filter: 'wa',
    },
    {
      label: 'Não conseguem entrar',
      tone: 'crit',
      value: views.filter((v) => (v.owner.label === 'Bloqueado' || v.owner.label === 'Sem proprietário') && v.model.active).length,
      hint: reportsLogin ? undefined : 'Conta os estúdios sem proprietário. A verificação de acesso do proprietário depende da atualização da API da lista de estúdios.',
      filter: 'login',
    },
    {
      label: 'Problemas de cobrança',
      tone: 'warn',
      value: views.filter((v) => ['past_due', 'awaiting_payment'].includes(v.model.billing)).length,
      filter: 'billing',
    },
    { label: 'Estúdios ativos', tone: 'ok', value: views.filter((v) => v.model.active).length, filter: 'all' },
  ]

  const attention = attentionViews
    .flatMap((v) =>
      v.issues
        .filter((i) => i.tone !== 'muted')
        .slice(0, 2)
        .map((i) => ({ ...i, view: v }))
    )
    .sort((a, b) => RANK[b.tone] - RANK[a.tone])
    .slice(0, 7)

  const tenantName = Object.fromEntries(tenants.map((t) => [t.id, t.name]))
  const last24h = chats ? chats.filter((c) => Date.now() - new Date(c.created_at) < 86400000).length : null
  const today = new Date().toLocaleDateString('pt-BR', { weekday: 'long', month: 'short', day: 'numeric' })

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Visão geral do suporte"
        subtitle={[
          today,
          loading ? null : `${attentionViews.length} ${attentionViews.length === 1 ? 'estúdio precisa' : 'estúdios precisam'} de atenção`,
          last24h == null ? null : `${last24h} ${last24h === 1 ? 'conversa' : 'conversas'} de suporte nas últimas 24h`,
        ]
          .filter(Boolean)
          .join(' · ')}
      />

      {error && <ErrorBanner>Não foi possível carregar os estúdios. {error.response?.data?.error?.message || error.message}</ErrorBanner>}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3">
        {kpis.map((k) => (
          <KpiCard
            key={k.label}
            {...k}
            value={loading ? '…' : k.value}
            onClick={() => navigate(k.filter === 'all' ? '/tenants' : `/tenants?filter=${k.filter}`)}
          />
        ))}
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))] items-start gap-4">
        <SectionCard
          title="Precisam de atenção"
          aside={
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/tenants?filter=attention')}>
              Todos os estúdios <ArrowRight className="h-4 w-4" />
            </button>
          }
        >
          {loading ? (
            <Spinner />
          ) : attention.length === 0 ? (
            <p className="px-1 py-2 text-[13px] text-admin-600">Nada precisa de atenção no momento.</p>
          ) : (
            attention.map((a, i) => (
              <button
                key={`${a.view.model.id}-${i}`}
                type="button"
                onClick={() => navigate(`/tenants/${a.view.model.id}${a.tab ? `?tab=${a.tab}` : ''}`)}
                className="grid grid-cols-[10px_minmax(0,1fr)_auto] items-start gap-3 rounded-md bg-admin-50 px-3 py-2.5 text-left hover:bg-admin-100"
              >
                <Dot tone={a.tone} size={10} className="mt-[5px]" />
                <span className="flex min-w-0 flex-col gap-px">
                  <span className="font-semibold">{a.title}</span>
                  <span className="text-[13px] text-admin-600">
                    {a.view.model.name} · {a.detail}
                  </span>
                </span>
                <span className="whitespace-nowrap text-xs text-admin-500">
                  {a.view.model.lastActive ? formatRelative(a.view.model.lastActive) : ''}
                </span>
              </button>
            ))
          )}
        </SectionCard>

        <SectionCard
          title="Conversas de suporte recentes"
          aside={
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/support-analysis')}>
              Abrir caixa de entrada <ArrowRight className="h-4 w-4" />
            </button>
          }
        >
          {chats == null ? (
            <Spinner />
          ) : chats.length === 0 ? (
            <p className="px-1 py-2 text-[13px] text-admin-600">Nenhuma conversa de suporte desde ontem.</p>
          ) : (
            chats.slice(0, 4).map((c) => {
              const isPublic = c.chat_type === 'public'
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => navigate(`/support-analysis?chat=${c.id}`)}
                  className="flex flex-col gap-0.5 rounded-md bg-admin-50 px-3 py-2.5 text-left hover:bg-admin-100"
                >
                  <span className="flex items-center gap-2">
                    <span className="flex-1 truncate font-semibold">
                      {isPublic
                        ? 'Visitante da documentação'
                        : `${c.user_name || 'Usuário desconhecido'}${tenantName[c.tenant_id] ? ` · ${tenantName[c.tenant_id]}` : ''}`}
                    </span>
                    <Tag tone={isPublic ? 'muted' : 'ok'}>{isPublic ? 'Pública' : 'Estúdio'}</Tag>
                    <span className="whitespace-nowrap text-xs text-admin-500">{formatRelative(c.created_at)}</span>
                  </span>
                  {firstUserMessage(c) && (
                    <span className="truncate text-[13px] text-admin-600">“{firstUserMessage(c)}”</span>
                  )}
                </button>
              )
            })
          )}
        </SectionCard>
      </div>
    </div>
  )
}
